import {
  defineComponent,
  Fragment,
  getCurrentInstance,
  h,
  nextTick,
  onMounted,
  onUpdated,
  provide,
  ref,
  renderSlot,
  watch,
} from 'vue'
import { isPromise, NOOP } from '@vue/shared'
import { buildProps, definePropType } from '@sgui-plus/utils/props'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { Awaitable } from '@sgui-plus/utils/types'
import { isString, isNumber } from '@sgui-plus/utils/util'
import { TabRootContextKey } from './constants'
import TabNav from './tab-nav'
import type { TabPaneContext } from './constants'

import type {
  Component,
  ComponentInternalInstance,
  VNode,
  ExtractPropTypes,
  Ref,
} from 'vue'

export type TabPaneName = string | number

export const tabProps = buildProps({
  type: {
    type: String,
    values: ['card', 'border-card', 'button', 'button-round', ''],
    default: '',
  },
  activeName: {
    type: [String, Number],
    default: '',
  },
  closable: Boolean,
  modelValue: {
    type: [String, Number],
    default: '',
  },
  tabPosition: {
    type: String,
    values: ['top', 'right', 'bottom', 'left'],
    default: 'top',
  },
  beforeLeave: {
    type: definePropType<
      (newTabName: TabPaneName, oldTabName: TabPaneName) => Awaitable<void | boolean>
    >(Function),
    default: () => true,
  },
  stretch: Boolean,
  labelWidth: {
    type: definePropType<number | string>([Number, String])
  }
} as const)
export type TabProps = ExtractPropTypes<typeof tabProps>

const isPaneName = (value: unknown): value is string | number => isString(value) || isNumber(value)

export const tabEmits = {
  [UPDATE_MODEL_EVENT]: (tabName: TabPaneName) => isPaneName(tabName),
  'tab-change': (tabName: TabPaneName) => isPaneName(tabName),
  'tab-click': (tabName: TabPaneName, pane: TabPaneContext, ev: Event) => ev instanceof Event,
  'tab-dblclick': (tabName: TabPaneName, pane: TabPaneContext, ev: Event) => ev instanceof Event,
  'tab-remove': (tabName: TabPaneName) => isPaneName(tabName),
  'tab-add': () => true,
}
export type TabEmits = typeof tabEmits

const getPaneInstanceFromSlot = (
  vnode: VNode,
  paneInstanceList: ComponentInternalInstance[] = []
) => {
  const children = (vnode.children || []) as ArrayLike<VNode>
  Array.from(children).forEach((node) => {
    let type = node.type
    type = (type as Component).name || type
    if (type === 'SgTabPane' && node.component) {
      paneInstanceList.push(node.component)
    } else if (type === Fragment || type === 'template') {
      getPaneInstanceFromSlot(node, paneInstanceList)
    }
  })
  return paneInstanceList
}

export default defineComponent({
  name: 'SgTab',

  props: tabProps,
  emits: tabEmits,

  setup(props, { emit, slots, expose }) {
    const instance = getCurrentInstance()!
    const nav$ = ref<InstanceType<typeof TabNav>>()

    const panes: Ref<TabPaneContext[]> = ref([])
    const currentName = ref(props.modelValue || props.activeName || '0')

    const paneStatesMap: Record<number, TabPaneContext> = {}

    const updatePaneInstances = (isForceUpdate = false) => {
      if (slots.default) {
        const children = instance.subTree.children as ArrayLike<VNode>
        const content = Array.from(children).find(
          ({ props }) => props?.class === 'sg-tab__content'
        )
        if (!content) return

        const paneInstanceList: TabPaneContext[] = getPaneInstanceFromSlot(
          content
        ).map((paneComponent) => paneStatesMap[paneComponent.uid])

        const panesChanged = !(
          paneInstanceList.length === panes.value.length &&
          paneInstanceList.every(
            (pane, index) => pane.uid === panes.value[index].uid
          )
        )

        if (isForceUpdate || panesChanged) {
          panes.value = paneInstanceList
        }
      } else if (panes.value.length !== 0) {
        panes.value = []
      }
    }

    const changeCurrentName = (value: TabPaneName) => {
      currentName.value = value
      emit(UPDATE_MODEL_EVENT, value)
      emit('tab-change', value)
    }

    const setCurrentName = (value: TabPaneName) => {
      // should do nothing.
      if (currentName.value === value) return

      const canLeave = props.beforeLeave?.(value, currentName.value)
      if (isPromise(canLeave)) {
        canLeave.then(
          () => {
            changeCurrentName(value)

            // call exposed function, Vue doesn't support expose in typescript yet.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            nav$.value?.removeFocus?.()
          },
          // ignore promise rejection in `before-leave` hook
          NOOP
        )
      } else if (canLeave !== false) {
        changeCurrentName(value)
      }
    }

    const handleTabClick = (
      tab: TabPaneContext,
      tabName: TabPaneName,
      event: Event
    ) => {
      if (tab.props.disabled) return
      setCurrentName(tabName)
      emit('tab-click', tabName, tab, event)
    }

    const handleTabDblclick = (
      tab: TabPaneContext,
      tabName: TabPaneName,
      event: Event
    ) => {
      if (tab.props.disabled) return
      emit('tab-dblclick', tabName, tab, event)
    }

    const handleTabRemove = (pane: TabPaneContext, ev: Event) => {
      if (pane.props.disabled) return
      ev.stopPropagation()
      emit('tab-remove', pane.props.name)
    }

    onUpdated(() => updatePaneInstances())
    onMounted(() => updatePaneInstances())

    watch(
      () => props.activeName,
      (modelValue) => setCurrentName(modelValue)
    )

    watch(
      () => props.modelValue,
      (modelValue) => setCurrentName(modelValue)
    )

    watch(currentName, async () => {
      updatePaneInstances(true)
      await nextTick()
      await nav$.value?.$nextTick()

      // call exposed function, Vue doesn't support expose in typescript yet.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      nav$.value?.scrollToActiveTab()
    })

    provide(TabRootContextKey, {
      props,
      currentName,
      updatePaneState: (pane) => (paneStatesMap[pane.uid] = pane),
    })

    expose({
      currentName,
    })

    return () => {
      const header = h(
        'div',
        { class: ['sg-tab__header', `is-${props.tabPosition}`] },
        [
          h(TabNav, {
            currentName: currentName.value,
            closable: props.closable,
            type: props.type,
            panes: panes.value,
            stretch: props.stretch,
            labelWidth: props.labelWidth,
            ref: nav$,
            onTabClick: handleTabClick,
            onTabDblclick: handleTabDblclick,
            onTabRemove: handleTabRemove,
          }),
        ]
      )

      const panels = h('div', { class: 'sg-tab__content' }, [
        renderSlot(slots, 'default'),
      ])

      return h(
        'div',
        {
          class: {
            'sg-tab': true,
            [`sg-tab--${props.type}`]: props.type,
            [`sg-tab--${props.tabPosition}`]: true
          },
        },
        props.tabPosition !== 'bottom' ? [header, panels] : [panels, header]
      )
    }
  },
})
